import React, { useState } from "react";
import Navbar from "./Navbar";
import "./App.css";

const steps = [
    {
        number: "01",
        title: "Open Vicka in Safari",
        description:
            "Make sure you're using Safari — this is the only browser on iPhone that supports adding websites to your Home Screen. Open Safari and go to vicka.ai.",
        tip: "Chrome, Firefox, and other browsers on iPhone do not support this feature.",
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="13" stroke="#4a7094" strokeWidth="1.5" />
                <path d="M14 7 L14 14 L19 17" stroke="#4a7094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="14" cy="14" r="2" fill="#4a7094" />
            </svg>
        ),
    },
    {
        number: "02",
        title: "Tap the Share button",
        description:
            "At the bottom of the Safari screen, tap the Share button — it looks like a box with an arrow pointing upward. This opens the Share Sheet menu.",
        tip: "If you don't see it, scroll down slightly — the toolbar may be hidden.",
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="7" y="13" width="14" height="10" rx="2" stroke="#4a7094" strokeWidth="1.5" />
                <path d="M14 4 L14 16 M10 8 L14 4 L18 8" stroke="#4a7094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        number: "03",
        title: '"Add to Home Screen"',
        description:
            'Scroll down in the Share Sheet until you see "Add to Home Screen." Tap it. If you don\'t see it right away, swipe left on the row of app icons, then look in the list below.',
        tip: "It's usually in the second row of options, below the app icon row.",
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="5" y="5" width="18" height="18" rx="3" stroke="#4a7094" strokeWidth="1.5" />
                <path d="M14 10 L14 18 M10 14 L18 14" stroke="#4a7094" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        number: "04",
        title: "Name it & confirm",
        description:
            'A screen will appear letting you name the shortcut. You can leave it as "Vicka" or type any name you like. Then tap "Add" in the top right corner.',
        tip: 'We recommend keeping the name "Vicka" so it\'s easy to find.',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M5 14 L11 20 L23 8" stroke="#4a7094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        number: "05",
        title: "You're all set!",
        description:
            "Vicka now lives on your Home Screen just like a native app — no App Store needed. Tap the icon anytime to open her instantly, full screen, without the browser bar.",
        tip: "You can move the icon to your dock or a folder just like any other app.",
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M14 3 L17.5 10.5 L26 11.8 L20 17.6 L21.4 26 L14 22.1 L6.6 26 L8 17.6 L2 11.8 L10.5 10.5 Z" stroke="#4a7094" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
        ),
    },
];

export default function SaveAsApp() {
    const [activeStep, setActiveStep] = useState(null);

    return (
        <div style={{ backgroundColor: "#f7f9fb", minHeight: "100vh" }}>
            <Navbar />

            <div style={{ maxWidth: "760px", margin: "0 auto", padding: "80px 24px 100px" }}>

                {/* Skip button - for new users coming from signup */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
                    <a
                        href="https://vicktoria.ai"
                        style={{
                            fontSize: "14px",
                            color: "#4a7094",
                            textDecoration: "none",
                            padding: "8px 16px",
                            border: "1px solid #c8dae5",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                        }}
                    >
                        Skip, take me to Vicktoria →
                    </a>
                </div>

                {/* Header */}
                <div style={{ marginBottom: "64px" }}>
                    <p style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#4a7094",
                        marginBottom: "16px",
                    }}>
                        Getting Started · iPhone Guide
                    </p>
                    <h1 style={{
                        fontSize: "clamp(28px, 5vw, 42px)",
                        fontWeight: "700",
                        color: "#1a2d3d",
                        lineHeight: "1.2",
                        marginBottom: "20px",
                        letterSpacing: "-0.02em",
                    }}>
                        Save Vicka to Your Home Screen
                    </h1>
                    <p style={{
                        fontSize: "17px",
                        color: "#4a5e6e",
                        lineHeight: "1.7",
                        maxWidth: "580px",
                    }}>
                        Get instant access to Vicka from your iPhone — no App Store download needed.
                        Follow these 5 simple steps and she'll be on your Home Screen in under a minute.
                    </p>

                    {/* Requirements badge */}
                    <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        marginTop: "24px",
                        padding: "10px 16px",
                        backgroundColor: "#e8f0f5",
                        borderRadius: "8px",
                        border: "1px solid #c8dae5",
                    }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="7" stroke="#4a7094" strokeWidth="1.2" />
                            <path d="M8 5v4M8 11v.5" stroke="#4a7094" strokeWidth="1.2" strokeLinecap="round" />
                        </svg>
                        <span style={{ fontSize: "13px", color: "#4a7094", fontWeight: "500" }}>
                            Requires iPhone with Safari browser
                        </span>
                    </div>
                </div>

                {/* Steps */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {steps.map((step, index) => {
                        const isOpen = activeStep === index;
                        return (
                            <div
                                key={index}
                                onClick={() => setActiveStep(isOpen ? null : index)}
                                style={{
                                    backgroundColor: "#ffffff",
                                    border: isOpen ? "1.5px solid #4a7094" : "1.5px solid #dce8ef",
                                    borderRadius: "14px",
                                    padding: "24px 28px",
                                    cursor: "pointer",
                                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                                    boxShadow: isOpen ? "0 4px 20px rgba(74,112,148,0.10)" : "none",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    <span style={{
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        color: "#B3CFDB",
                                        letterSpacing: "0.06em",
                                        minWidth: "24px",
                                    }}>
                                        {step.number}
                                    </span>

                                    <div style={{
                                        width: "44px",
                                        height: "44px",
                                        borderRadius: "10px",
                                        backgroundColor: "#eef4f8",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}>
                                        {step.icon}
                                    </div>

                                    <h3 style={{
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        color: "#1a2d3d",
                                        margin: 0,
                                        flex: 1,
                                    }}>
                                        {step.title}
                                    </h3>

                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        style={{
                                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.2s ease",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <path
                                            d="M5 7.5 L10 12.5 L15 7.5"
                                            stroke="#4a7094"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>

                                {isOpen && (
                                    <div style={{ marginTop: "20px", paddingLeft: "84px" }}>
                                        <p style={{
                                            fontSize: "15px",
                                            color: "#3a4f5e",
                                            lineHeight: "1.7",
                                            marginBottom: "14px",
                                        }}>
                                            {step.description}
                                        </p>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            padding: "10px 14px",
                                            backgroundColor: "#f0f6fa",
                                            borderRadius: "8px",
                                            borderLeft: "3px solid #B3CFDB",
                                        }}>
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginTop: "2px", flexShrink: 0 }}>
                                                <circle cx="7" cy="7" r="6" stroke="#4a7094" strokeWidth="1" />
                                                <path d="M7 4.5v3.5M7 9.5v.5" stroke="#4a7094" strokeWidth="1" strokeLinecap="round" />
                                            </svg>
                                            <p style={{
                                                fontSize: "13px",
                                                color: "#4a7094",
                                                lineHeight: "1.6",
                                                margin: 0,
                                            }}>
                                                {step.tip}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Bottom CTA */}
                <div style={{
                    marginTop: "56px",
                    padding: "36px 40px",
                    backgroundColor: "#D1DEE6",
                    borderRadius: "16px",
                    textAlign: "center",
                }}>
                    <h3 style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1a2d3d",
                        marginBottom: "10px",
                    }}>
                        Need more help?
                    </h3>
                    <p style={{
                        fontSize: "15px",
                        color: "#3a4f5e",
                        marginBottom: "24px",
                        lineHeight: "1.6",
                    }}>
                        If you run into any trouble, our team is happy to help you get set up.
                    </p>
                    <a
                        href="/contact"
                        style={{
                            display: "inline-block",
                            padding: "12px 28px",
                            backgroundColor: "#4a7094",
                            color: "#ffffff",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            textDecoration: "none",
                            letterSpacing: "0.02em",
                        }}
                    >
                        Contact Support
                    </a>
                </div>
            </div>
        </div>
    );
}